import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import TaskFilterControls, {
  DEFAULT_FILTERS,
} from "../KeyComponents/TaskFilterControls";
import { useWorkspace } from "../../utils/WorkspaceContext";
import { useCustomViews } from "../../utils/CustomViewsProvider";
import useTaskFilterOptions from "../../utils/useTaskFilterOptions";
import { DEFAULT_TABLE_COLUMNS } from "../../utils/tableColumns";

const VIEW_LABELS = { list: "List", table: "Table", kanban: "Kanban" };

const emptyConfig = () => ({
  filters: { ...DEFAULT_FILTERS },
  sort: [{ field: "line_up", dir: "asc" }],
  columns: { ...DEFAULT_TABLE_COLUMNS },
  views: { list: true, table: true, kanban: false },
  defaultView: "list",
});

// Create/edit dialog for a CustomView. Pass `view` to edit, omit to create.
const CustomViewDialog = ({ open, onClose, view = null, onSaved }) => {
  const { currentWorkspace } = useWorkspace();
  const { createView, updateView } = useCustomViews();
  const { sprintOptions, projectOptions, epicOptions, trackOptions } =
    useTaskFilterOptions(currentWorkspace?.id);

  const [name, setName] = useState("");
  const [config, setConfig] = useState(emptyConfig());
  const [saving, setSaving] = useState(false);

  // Seed the form whenever the dialog opens (create vs edit).
  useEffect(() => {
    if (!open) return;
    if (view) {
      setName(view.name || "");
      setConfig({ ...emptyConfig(), ...(view.config || {}) });
    } else {
      setName("");
      setConfig(emptyConfig());
    }
  }, [open, view]);

  const enabledViews = config.views || {};
  const enabledList = useMemo(
    () => Object.keys(VIEW_LABELS).filter((k) => enabledViews[k]),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [config.views],
  );

  const setFilters = (patch) =>
    setConfig((c) => ({ ...c, filters: { ...c.filters, ...patch } }));
  const setSort = (sort) => setConfig((c) => ({ ...c, sort }));

  const toggleView = (key) =>
    setConfig((c) => {
      const views = { ...c.views, [key]: !c.views[key] };
      // Keep defaultView pointing at an enabled view.
      let defaultView = c.defaultView;
      if (!views[defaultView]) {
        defaultView = Object.keys(VIEW_LABELS).find((k) => views[k]) || "list";
      }
      return { ...c, views, defaultView };
    });

  const canSave =
    name.trim().length > 0 && enabledList.length > 0 && !saving;

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      const payload = { name: name.trim(), config };
      const saved = view
        ? await updateView(view.id, payload)
        : await createView(payload);
      onSaved?.(saved);
      onClose?.();
    } catch (error) {
      console.error("Error saving custom view:", error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{view ? "Edit Personalized View" : "New Personalized View"}</DialogTitle>
      <DialogContent dividers>
        <TextField
          autoFocus
          fullWidth
          label="View name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          sx={{ mb: 3, mt: 1 }}
        />

        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Filters &amp; sort
        </Typography>
        <Box sx={{ p: 1.5, backgroundColor: "action.hover", borderRadius: 1, mb: 3 }}>
          <TaskFilterControls
            filters={config.filters}
            onChange={setFilters}
            sprintOptions={sprintOptions}
            projectOptions={projectOptions}
            epicOptions={epicOptions}
            trackOptions={trackOptions}
            sortCriteria={config.sort}
            onSortChange={setSort}
          />
        </Box>

        <Divider sx={{ mb: 2 }} />

        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Views
        </Typography>
        <FormGroup row sx={{ mb: 2 }}>
          {Object.entries(VIEW_LABELS).map(([key, label]) => (
            <FormControlLabel
              key={key}
              control={
                <Checkbox
                  checked={Boolean(enabledViews[key])}
                  onChange={() => toggleView(key)}
                />
              }
              label={label}
            />
          ))}
        </FormGroup>

        <FormControl size="small" sx={{ minWidth: 200 }} disabled={enabledList.length === 0}>
          <InputLabel>Default view</InputLabel>
          <Select
            value={enabledList.includes(config.defaultView) ? config.defaultView : (enabledList[0] || "")}
            label="Default view"
            onChange={(e) => setConfig((c) => ({ ...c, defaultView: e.target.value }))}
          >
            {enabledList.map((k) => (
              <MenuItem key={k} value={k}>{VIEW_LABELS[k]}</MenuItem>
            ))}
          </Select>
        </FormControl>

        {enabledList.length === 0 && (
          <Typography variant="caption" color="error" sx={{ display: "block", mt: 1 }}>
            Enable at least one view.
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={!canSave}>
          {view ? "Save changes" : "Create"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CustomViewDialog;
